import React from 'react';
import { listChannelAccounts } from '../services/channelAccountService';
import { ChannelAccount, ChannelAccountChannel, ChannelAccountLimits } from '../types/channelAccount';

export const useChannelAccounts = (
  channel: ChannelAccountChannel,
  enabled: boolean,
  connectionKind?: 'cloud' | 'app',
) => {
  const [accounts, setAccounts] = React.useState<ChannelAccount[]>([]);
  const [limits, setLimits] = React.useState<ChannelAccountLimits | null>(null);
  const [selectedAccountId, setSelectedAccountId] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const reload = React.useCallback(async () => {
    if (!enabled) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await listChannelAccounts(channel, connectionKind);
      setAccounts(response.items);
      setLimits(response.limits);
      setSelectedAccountId(previous => {
        if (previous && response.items.some(account => account.id === previous)) {
          return previous;
        }

        const defaultAccount = response.items.find(account => account.isDefault) ?? response.items[0];
        return defaultAccount?.id ?? null;
      });
    } catch (loadError) {
      setAccounts([]);
      setLimits(null);
      setError(loadError instanceof Error ? loadError.message : 'Failed to load channel accounts.');
    } finally {
      setLoading(false);
    }
  }, [channel, connectionKind, enabled]);

  React.useEffect(() => {
    void reload();
  }, [reload]);

  const selectedAccount = React.useMemo(
    () => accounts.find(account => account.id === selectedAccountId) ?? null,
    [accounts, selectedAccountId],
  );

  return {
    accounts,
    limits,
    selectedAccount,
    selectedAccountId,
    setSelectedAccountId,
    loading,
    error,
    reload,
  };
};
